import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Database, Loader2 } from 'lucide-react';

export default function Register() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        company_name: ''
    });
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.password !== formData.confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }

        if (formData.password.length < 8) {
            toast.error('Le mot de passe doit contenir au moins 8 caractères');
            return;
        }

        setLoading(true);

        try {
            await register(
                formData.email,
                formData.password,
                formData.name,
                formData.company_name
            );
            toast.success('Compte créé avec succès');
            navigate('/dashboard');
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la création du compte';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Image */}
            <div 
                className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1533777243653-9dfe89d32ce3?crop=entropy&cs=srgb&fm=jpg&q=85)'
                }}
            >
                <div className="absolute inset-0 bg-black/80" />
                <div className="relative z-10 flex flex-col justify-end p-12">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="flex items-center justify-center w-12 h-12 rounded bg-primary/20">
                            <Database className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl font-bold tracking-tight text-white">
                                AD-MASTER
                            </h1>
                            <p className="text-sm text-zinc-400">Gestion de stock DCP</p>
                        </div>
                    </div>
                    <p className="text-lg text-zinc-300 max-w-md">
                        Créez votre compte entreprise et commencez à gérer 
                        vos archives DCP dès aujourd'hui.
                    </p>
                </div>
            </div>

            {/* Right side - Form */}
            <div className="flex-1 flex items-center justify-center p-6 bg-background">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="flex items-center gap-3 mb-8 lg:hidden">
                        <div className="flex items-center justify-center w-10 h-10 rounded bg-primary/20">
                            <Database className="w-5 h-5 text-primary" />
                        </div>
                        <h1 className="font-heading text-2xl font-bold tracking-tight">
                            AD-MASTER
                        </h1>
                    </div>

                    <Card className="border-border bg-card">
                        <CardHeader className="space-y-1">
                            <CardTitle className="font-heading text-2xl">Créer un compte</CardTitle>
                            <CardDescription>
                                Inscrivez votre société pour commencer
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="company_name">Nom de la société</Label>
                                    <Input
                                        id="company_name"
                                        name="company_name"
                                        placeholder="Ma Société Production"
                                        value={formData.company_name}
                                        onChange={handleChange}
                                        required
                                        data-testid="register-company-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="name">Votre nom</Label>
                                    <Input
                                        id="name"
                                        name="name"
                                        placeholder="Jean Dupont"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        data-testid="register-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        placeholder="votre@email.com"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        data-testid="register-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password">Mot de passe</Label>
                                    <Input
                                        id="password"
                                        name="password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        data-testid="register-password-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                                    <Input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type="password"
                                        placeholder="••••••••"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        required
                                        data-testid="register-confirm-password-input"
                                    />
                                </div>
                                <Button 
                                    type="submit" 
                                    className="w-full font-semibold uppercase tracking-wider"
                                    disabled={loading}
                                    data-testid="register-submit-button"
                                >
                                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Créer mon compte
                                </Button>
                            </form>

                            <div className="mt-6 text-center text-sm">
                                <span className="text-muted-foreground">Déjà un compte ? </span>
                                <Link 
                                    to="/login" 
                                    className="text-primary hover:underline font-medium"
                                    data-testid="login-link"
                                >
                                    Se connecter
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
